// BankIdGate — wrapper som blokkerer barn til bruker har alders-verifisering.
// Godtar enten `verification_level >= 1` (kjøpt tokens via kort/Strex) eller
// `bankid_verified === true` (egen BankID-flyt).
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Layout from "./Layout";

export default function BankIdGate({ children, nextUrl }) {
  const location = useLocation();
  const [status, setStatus] = useState(null); // null=loading, obj=loaded
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/me/bankid/status");
        if (mounted) setStatus(r.data);
      } catch {
        if (mounted) setStatus({ glimt_eligible: false, bankid_verified: false, verification_level: 0 });
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // Vis kvittering hvis vi kom tilbake fra BankID-flyten
    const params = new URLSearchParams(location.search);
    const b = params.get("bankid");
    if (!b) return;
    if (b === "ok") toast.success("BankID-verifisering fullført");
    else if (b === "aborted") toast.info("Du avbrøt BankID-flyten");
    else if (b === "lookup_failed") toast.error("BankID-oppslag feilet");
    else if (b === "config_error") toast.error("BankID er ikke konfigurert – kontakt support");
  }, [location.search]);

  const startBankId = async () => {
    setStarting(true);
    try {
      const r = await api.post("/me/bankid/start", { next_url: nextUrl || location.pathname });
      if (r.data.already_verified) {
        setStatus((s) => ({ ...(s || {}), bankid_verified: true, glimt_eligible: true }));
        return;
      }
      if (r.data.redirect_url) {
        window.location.href = r.data.redirect_url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setStarting(false);
    }
  };

  if (status === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="bankid-gate-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  if (status.glimt_eligible) {
    return <>{children}</>;
  }

  // Gate-skjerm
  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100dvh-4rem)] flex items-center justify-center p-6 bg-black">
        <div className="max-w-md w-full panel-card p-8 text-center space-y-6" data-testid="bankid-gate-block">
          <div className="w-16 h-16 mx-auto rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/40 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-[var(--gold)]" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-fraunces text-[var(--cream)]">Glimt krever alders-verifisering</h1>
            <p className="text-[var(--cream-soft)] text-sm leading-relaxed">
              For å bruke Glimt må vi bekrefte at du er over 18. Du har to alternativer:
            </p>
          </div>

          <div className="text-left space-y-3 text-sm">
            <div className="p-4 border border-[var(--magenta)]/30 bg-[var(--magenta)]/5">
              <p className="font-fraunces text-[var(--magenta)] mb-1">Alternativ 1 — BankID</p>
              <p className="text-[var(--cream-soft)] text-xs">
                Rask, gratis for deg. Vi lagrer ikke fødselsnummer, kun navn + verifisert alder.
              </p>
            </div>
            <div className="p-4 border border-[var(--gold)]/30 bg-[var(--gold)]/5">
              <p className="font-fraunces text-[var(--gold)] mb-1">Alternativ 2 — Kjøp tokens</p>
              <p className="text-[var(--cream-soft)] text-xs">
                Én token-pakke via kort eller Strex teller også som alders-verifisering (Strex krever 18+).
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={startBankId}
              disabled={starting}
              className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-3"
              data-testid="bankid-gate-start-btn"
            >
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {starting ? "Starter BankID…" : "Verifiser med BankID"}
            </button>
            <Link
              to="/kjop"
              className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-colors"
              data-testid="bankid-gate-buy-tokens-btn"
            >
              Kjøp tokens i stedet
            </Link>
          </div>

          <p className="text-[10px] text-[var(--cream-faint)]">
            BankID leveres av Strex (Target365). Vi tar aldri betalt av deg for verifiseringen.
          </p>
        </div>
      </div>
    </Layout>
  );
}
